<template>
  <div :class="$style.page">
    <section :class="$style.page__section">
      <header :class="$style.header">
        <SectionTitle :class="[$style.header__title, $style.title]"
          >お悩み別ユーザーリスト</SectionTitle
        >
      </header>
      <div :class="$style.section__contents">
        <KarteFilterPresetList />
      </div>
    </section>

    <section :class="$style.page__section">
      <header :class="$style.header">
        <SectionTitle :class="[$style.header__title, $style.title]"
          >あなたがアプローチできるユーザー</SectionTitle
        >
        <KartePlannerCommentMonthlyAmount
          :class="$style.header__counters"
          v-if="
            shouldShowKartePlannerCommentMonthlyAmount &&
            kartePlannerCommentMonthlyAmount
          "
          :kartePlannerCommentMonthlyAmount="kartePlannerCommentMonthlyAmount"
        />
      </header>
      <div :class="[$style.karte, $style.section__contents]">
        <div :class="$style.karte__filter">
          <KarteUserFilteringItemList
            :class="$style.filter"
            @change.self="handleChangeFilteringRule"
          />
        </div>
        <div :class="[$style.karte__content, $style.list]">
          <VerticalScrollEdgeDetector
            v-if="!shouldShowEmptyView"
            :detectorHeight="200"
            @bottomStateChange="
              onKarteUserListScrollBottomStateChange($event.onBottom)
            "
            :class="$style.list__content"
          >
            <div
              v-if="isKartenListInitialLoading"
              :class="$style.list__loadingWrapper"
            >
              <!-- FIXME: KarteUserList の中でも LoadingIndicator を持っている。まとめてほしい -->
              <LoadingIndicator />
            </div>
            <KarteUserList
              v-else
              :karten="karten"
              :hasNext="hasNext ?? false"
            />
          </VerticalScrollEdgeDetector>
          <KarteEmptyUserList v-else :class="$style.list__empty">
            現在、対応可能地域でアプローチできるユーザーがいません。<br />
            絞り込みをしている場合は、条件を変更してみましょう。
          </KarteEmptyUserList>
        </div>
      </div>
      <AppSlideModal
        @close="handleClose"
        :shown="maybeKarteDetailRoute != undefined"
      >
        <KarteUserDetailView
          v-if="maybeKarteDetailRoute != undefined"
          :class="$style.detail"
          @close="handleClose"
          :karteId="Number.parseInt(maybeKarteDetailRoute.params.karteId)"
        />
      </AppSlideModal>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import KarteUserList from './KarteUserList/KarteUserList.vue'
import KarteEmptyUserList from './KarteUserList/KarteEmptyUserList.vue'
import KarteUserFilteringItemList from './KarteUserFilteringItemList/KarteUserFilteringItemList.vue'
import KarteUserDetailView from './KarteUserDetail/KarteUserDetailView.vue'
import KarteFilterPresetList from './KarteFilterPresetList/KarteFilterPresetList.vue'
import AppSlideModal from '@/components/molecules/AppSlideModal.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import { useRouter, useRoute } from 'vue-router'
import KartePlannerCommentMonthlyAmount from './KartePlannerCommentMonthlyAmount/KartePlannerCommentMonthlyAmount.vue'
import { useLoadKarteListPageQuery } from './KarteListPage.generated'

import {
  convertFilteringRuleToGqlInputType,
  type KarteUserFilteringRule,
} from '@/types/app'
import { useHead } from '@unhead/vue'
import {
  type InputMaybe,
  type KartenFilter,
  Plan,
  SortKey,
} from '@/types/schema.generated'

import { useLazyKartenList } from './hooks/useKartenList'
import VerticalScrollEdgeDetector from '@/components/molecules/VerticalScrollEdgeDetector.vue'
import { useDocumentVisibility } from '@vueuse/core'
import { useLogger } from '@/logger/logger'
import { convertKarteUserFilteringRuleToLogData } from '@/logger/dataConvert'
import { useMaybeRoute } from '@/features/router/route'

useHead({
  title: 'ユーザーを探す',
})

const logger = useLogger()
const router = useRouter()
const route = useRoute('karteList')

const {
  result: kartePlannerCommentResult,
  refetch: kartePlannerCommentRefetch,
} = useLoadKarteListPageQuery()
const kartePlannerCommentMonthlyAmount = computed(
  () =>
    kartePlannerCommentResult.value?.viewer.kartePlannerCommentMonthlyAmount ??
    null
)

const shouldShowKartePlannerCommentMonthlyAmount = computed(
  () =>
    kartePlannerCommentMonthlyAmount.value != null &&
    kartePlannerCommentResult.value?.viewer.plan !== Plan.V2022_2ManageOnlyFree
)

const entranceGUID = computed(() => route.params.entranceGUID as string)
watch(entranceGUID, () => {
  kartePlannerCommentRefetch()
})

const maybeKarteDetailRoute = useMaybeRoute('karteDetail')

const handleClose = async () => {
  // eslint-disable-next-line fp/no-mutating-methods
  await router.push({
    name: 'karteList',
    query: route.query,
  })
}

const filter = ref<InputMaybe<KartenFilter>>(null)
const queryVariables = computed(() => ({
  sortedBy: SortKey.KarteScoreOriginal,
  first: 20,
  filter: filter.value == null ? null : { ...filter.value },
}))

const {
  karten,
  hasNext,
  initialLoading: isKartenListInitialLoading,
  fetchMore,
  refetch,
  load,
} = useLazyKartenList(queryVariables, () => ({
  // 検索条件の変更のたびに、キャッシュから取得しないようにする
  fetchPolicy: 'network-only',
}))

watch(queryVariables, v => refetch(v))
watch(entranceGUID, () => {
  kartePlannerCommentRefetch()
})

// スプレッドシート等による大量タブ開きによるカルテ取得エンドポイントDoS対策
const visibility = useDocumentVisibility()

// filterの値を利用したqueryVariablesが値を持った状態になってから初回読み込み
// filterをwatchすると、watcherの処理の関係上
// 実際にQueryが利用するqueryVariablesのフィールドに値が入る前にQueryを投げてしまうので注意
const unwatchInitialLoad = watch(
  [queryVariables, visibility],
  ([_variables, _visibility]) => {
    if (_visibility !== 'visible' || _variables.filter == null) {
      return
    }
    unwatchInitialLoad()
    load(undefined, _variables)
  }
)

const onKarteUserListScrollBottomStateChange = (onBottom: boolean) => {
  if (onBottom && hasNext.value) {
    fetchMore()
  }
}

const shouldShowEmptyView = computed(
  () => !isKartenListInitialLoading.value && !karten.value.length
)

const handleChangeFilteringRule = async (newRule: KarteUserFilteringRule) => {
  filter.value = convertFilteringRuleToGqlInputType(newRule)
  logger.ltlPeople.event.applyKarteUserFilteringRule(logger.buildMeta(), {
    data: convertKarteUserFilteringRuleToLogData(newRule),
  })
}
</script>

<style lang="scss" module>
.page {
  &__section {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.section {
  &__contents {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.karte {
  display: flex;
  align-items: flex-start;
  gap: 24px;

  &__filter {
    flex: 0 0 296px;
  }

  &__content {
    flex: 1;
  }
}

.detail {
  height: 100vh;
}

.header {
  display: flex;
  align-items: center;

  &__title {
    flex: 1;

    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }

  &__orderSelector {
    width: 160px;

    &:nth-child(n + 2) {
      margin-left: 24px;
    }
  }
}

.filter {
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-rounder;
  box-shadow: $shadow-20;
}

.list {
  &__empty {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
  }

  &__loadingWrapper {
    display: flex;
    justify-content: center;
    padding: 32px;
  }
}
</style>
